// src/ui/designTokens.ts — Traceable to NFR-STYLE-001, NFR-STYLE-002, NFR-TOUCH-001

export const tokens = {
  color: {
    // Victorian Deep-Sea Atelier Natural & Bioluminescent Pigments
    vellum:           '#FAF6ED',  // Aged botanical vellum / parchment canvas
    vellumAged:       '#EFE5CE',  // Deep parchment inner tone
    vellumEdge:       '#D5C5A3',  // Beveled card boundary
    morrisIndigo:     '#1A365D',  // Deep mineral ink, typography, woodcut linework
    indigoInk:        '#0F2342',  // Ultra-deep ink for maximum contrast & headings
    seaweedSage:      '#3D5A45',  // Morris acanthus/seaweed green, success states
    madderCoral:      '#FF6B4A',  // Vibrant oceanic coral & lobster shell accent
    madderRed:        '#9E3D2B',  // Deep terracotta
    madderTerracotta: '#B84228',  // Rich warm Victorian terracotta
    gildedBrass:      '#E5A93C',  // Gilded botanical borders, badge trims, highlights
    antiqueBrass:     '#C8963E',  // Antique brass hardware
    deepAbyss:        '#0A192F',  // Atmospheric ocean depths
    biolumCyan:       '#00E5FF',  // Shimmering bioluminescent spores & active clues
    chalkCream:       '#FFFDF9',  // Clean card interior for zero-strain reading
  },
  glass: {
    vellumCard:    'rgba(255, 253, 249, 0.94)',
    blurStrength:  '16px',
    sheenGradient: 'linear-gradient(135deg, rgba(255,255,255,0.7) 0%, rgba(255,255,255,0) 60%)',
    boxShadow3d:   'inset 0 0 36px rgba(166, 126, 56, 0.18), 0 4px 0 #D8C7A4, 0 7px 0 #9E7B42, 0 22px 48px rgba(4, 10, 22, 0.65)',
  },
  spacing: {
    touchMin: '48px',          // NFR-TOUCH-001 (ADHD & children ergonomics)
    cardPad:  '24px',
    gutter:   '16px',
    flourish: '32px',
  },
  font: {
    // Arts & Crafts woodblock character paired with high-legibility sans
    heading:  "'Fraunces', 'Cinzel', serif",           // Victorian naturalist titleplates
    ornate:   "'Cinzel', serif",                       // Ornate Roman inscriptions & badges
    body:     "'Nunito', sans-serif",                  // ADHD reading clarity & friendly KS2 rounded shapes
    mono:     "'JetBrains Mono', monospace",           // Coordinates, code, timer
    sizeMin:  '18px',                                  // FR-ENG-001
  },
  border: {
    woodblock: '2px solid #1A365D',
    doubleRule: '3px double #E5A93C',
    gildedGold: '1.5px solid rgba(229, 169, 60, 0.5)',
    radius: '12px',
  },
  breakpoint: {
    mobile:  '430px',
    tablet:  '768px',
    desktop: '1024px',
  },
} as const;

export type DesignTokens = typeof tokens;

export function injectThemeVariables(root: HTMLElement = document.documentElement): void {
  // Colors
  root.style.setProperty('--color-vellum', tokens.color.vellum);
  root.style.setProperty('--color-vellum-aged', tokens.color.vellumAged);
  root.style.setProperty('--color-vellum-edge', tokens.color.vellumEdge);
  root.style.setProperty('--color-morris-indigo', tokens.color.morrisIndigo);
  root.style.setProperty('--color-indigo-ink', tokens.color.indigoInk);
  root.style.setProperty('--color-seaweed-sage', tokens.color.seaweedSage);
  root.style.setProperty('--color-madder-coral', tokens.color.madderCoral);
  root.style.setProperty('--color-madder-red', tokens.color.madderRed);
  root.style.setProperty('--color-madder-terracotta', tokens.color.madderTerracotta);
  root.style.setProperty('--color-gilded-brass', tokens.color.gildedBrass);
  root.style.setProperty('--color-antique-brass', tokens.color.antiqueBrass);
  root.style.setProperty('--color-deep-abyss', tokens.color.deepAbyss);
  root.style.setProperty('--color-biolum-cyan', tokens.color.biolumCyan);
  root.style.setProperty('--color-chalk-cream', tokens.color.chalkCream);

  // Template compatible shorthand variables
  root.style.setProperty('--vellum', tokens.color.vellum);
  root.style.setProperty('--vellum-aged', tokens.color.vellumAged);
  root.style.setProperty('--vellum-edge', tokens.color.vellumEdge);
  root.style.setProperty('--morris-indigo', tokens.color.morrisIndigo);
  root.style.setProperty('--indigo-ink', tokens.color.indigoInk);
  root.style.setProperty('--seaweed-sage', tokens.color.seaweedSage);
  root.style.setProperty('--madder-coral', tokens.color.madderCoral);
  root.style.setProperty('--madder-terracotta', tokens.color.madderTerracotta);
  root.style.setProperty('--gilded-brass', tokens.color.gildedBrass);
  root.style.setProperty('--antique-brass', tokens.color.antiqueBrass);
  root.style.setProperty('--deep-abyss', tokens.color.deepAbyss);
  root.style.setProperty('--biolum-cyan', tokens.color.biolumCyan);
  root.style.setProperty('--chalk-cream', tokens.color.chalkCream);

  // Glassmorphic tokens
  root.style.setProperty('--glass-card-bg', tokens.glass.vellumCard);
  root.style.setProperty('--glass-blur', tokens.glass.blurStrength);
  root.style.setProperty('--glass-shadow-3d', tokens.glass.boxShadow3d);

  // Fonts
  root.style.setProperty('--font-heading', tokens.font.heading);
  root.style.setProperty('--font-ornate', tokens.font.ornate);
  root.style.setProperty('--font-body', tokens.font.body);
  root.style.setProperty('--font-mono', tokens.font.mono);
  root.style.setProperty('--font-size-min', tokens.font.sizeMin);

  // Spacing & Ergonomics
  root.style.setProperty('--touch-min', tokens.spacing.touchMin);
  root.style.setProperty('--card-pad', tokens.spacing.cardPad);
  root.style.setProperty('--gutter', tokens.spacing.gutter);
  root.style.setProperty('--flourish-size', tokens.spacing.flourish);

  // Borders
  root.style.setProperty('--border-woodblock', tokens.border.woodblock);
  root.style.setProperty('--border-double-rule', tokens.border.doubleRule);
  root.style.setProperty('--border-gilded-gold', tokens.border.gildedGold);
  root.style.setProperty('--border-radius', tokens.border.radius);
}
